import React, { useEffect, useState, useRef } from 'react';
import { MessageCircle, Star, Gift, Heart, Bell, X, Settings, ChevronRight, Trophy, Zap, Filter, Clock, Calendar, Flame, Crown, Sparkles, Bookmark, Eye, ChevronLeft } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { cn } from '../lib/utils';
interface NotificationCenterProps {
  isOpen: boolean;
  onClose: () => void;
  inSidebar?: boolean;
}
// Define notification types
type NotificationType = 'message' | 'achievement' | 'gift' | 'tokens' | 'reward' | 'premium' | 'fanclub' | 'quest' | 'feature' | 'behavioral';
interface Notification {
  id: number;
  type: NotificationType;
  title: string;
  content: string;
  time: string;
  read: boolean;
  image?: string;
  avatar?: string;
  sender?: string;
  amount?: number;
  cta?: string;
  action?: string;
  category?: 'premium' | 'reward' | 'fanclub' | 'quest';
}
export const NotificationCenter: React.FC<NotificationCenterProps> = ({
  isOpen,
  onClose,
  inSidebar = false
}) => {
  const [activeTab, setActiveTab] = useState<'all' | 'activity' | 'rewards'>('all');
  const [filter, setFilter] = useState<'all' | 'premium' | 'reward' | 'fanclub' | 'quest'>('all');
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [showAlert, setShowAlert] = useState(false);
  const [currentAlert, setCurrentAlert] = useState<Notification | null>(null);
  const filterBarRef = useRef<HTMLDivElement>(null);
  const scrollFilters = (direction: 'left' | 'right') => {
    if (filterBarRef.current) {
      const scrollAmount = direction === 'left' ? -100 : 100;
      filterBarRef.current.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }
  };
  useEffect(() => {
    // Simulate loading notifications
    const mockNotifications: Notification[] = [{
      id: 1,
      type: 'message',
      title: 'New message from Sophia',
      content: 'Hey there! I miss talking to you... Want to continue our conversation?',
      time: '2 min ago',
      read: false,
      avatar: 'https://uploadthingy.s3.us-west-1.amazonaws.com/bU73WvXeTZLjt1PKcu9KyE/image.png',
      sender: 'Sophia',
      cta: 'Reply Now',
      action: '/chat'
    }, {
      id: 2,
      type: 'achievement',
      title: '🏆 Achievement Unlocked: Passionate Lover',
      content: "You've chatted for 10 consecutive days. Your dedication is impressive!",
      time: '1 hour ago',
      read: false,
      cta: 'View Achievements',
      action: '/achievements',
      category: 'reward'
    }, {
      id: 3,
      type: 'premium',
      title: "🔥 Limited Release: Kim's New Story",
      content: "You're one of the few invited to see what she posted last night.",
      time: '3 hours ago',
      read: true,
      avatar: 'https://uploadthingy.s3.us-west-1.amazonaws.com/bZ3iVXv5nZEMY6F847weq5/kimholland.png',
      cta: 'Unlock Now',
      action: '/premium/kim-holland',
      category: 'premium'
    }, {
      id: 4,
      type: 'tokens',
      title: '🎉 You earned 200 LuvTokens!',
      content: 'Thanks for completing the Full Experience Questionnaire.',
      time: '5 hours ago',
      read: true,
      amount: 200,
      cta: 'View Rewards',
      action: '/rewards',
      category: 'reward'
    }, {
      id: 5,
      type: 'message',
      title: 'New message from Kim Holland',
      content: "Check out my new photos... I think you'll really like what you see.",
      time: 'Yesterday',
      read: true,
      avatar: 'https://uploadthingy.s3.us-west-1.amazonaws.com/bZ3iVXv5nZEMY6F847weq5/kimholland.png',
      sender: 'Kim Holland',
      cta: 'View Message',
      action: '/chat/kim-holland'
    }, {
      id: 6,
      type: 'fanclub',
      title: "❤️ You've unlocked Bonny Blue's fan club",
      content: 'Enter now to see exclusive content and interact with Bonny directly.',
      time: 'Yesterday',
      read: true,
      avatar: 'https://uploadthingy.s3.us-west-1.amazonaws.com/jq4Dkth7vgkmZJ8GZmzWDP/bonnyblue.png',
      cta: 'Join Fan Club',
      action: '/fanclub/bonny-blue',
      category: 'fanclub'
    }, {
      id: 7,
      type: 'behavioral',
      title: "👀 You've liked 3 kinky polls this week",
      content: "Want to see Bonnie Blue's roleplay fantasy? It matches your interests.",
      time: '2 days ago',
      read: true,
      avatar: 'https://uploadthingy.s3.us-west-1.amazonaws.com/jq4Dkth7vgkmZJ8GZmzWDP/bonnyblue.png',
      cta: 'See Fantasy',
      action: '/polls/kinky/bonnie-blue',
      category: 'premium'
    }, {
      id: 8,
      type: 'feature',
      title: '✨ New Feature: Bond Rituals',
      content: 'Build your emotional connection with custom ritual paths.',
      time: '2 days ago',
      read: true,
      cta: 'Try It Now',
      action: '/rituals',
      category: 'quest'
    }, {
      id: 9,
      type: 'quest',
      title: '🔮 Daily Quest: Share Your Fantasy',
      content: "Complete today's quest to earn 50 LuvTokens and deepen your connection.",
      time: '2 days ago',
      read: true,
      cta: 'Start Quest',
      action: '/quests/daily',
      category: 'quest'
    }, {
      id: 10,
      type: 'reward',
      title: '🎁 Daily Streak Reward Available',
      content: "You've maintained your streak for 3 days. Claim your bonus reward now.",
      time: '2 days ago',
      read: true,
      cta: 'Claim Reward',
      action: '/rewards/daily-streak',
      category: 'reward'
    },
    // Additional activity feed items
    {
      id: 11,
      type: 'message',
      title: 'Lana viewed your profile',
      content: 'She spent 5 minutes looking at your photos and information.',
      time: '30 min ago',
      read: false,
      avatar: 'https://uploadthingy.s3.us-west-1.amazonaws.com/cLp9w3hC4dVahxHuCeYw1i/488653244_669275086051024_406085801635574977_n.jpg',
      sender: 'Lana',
      cta: 'View Her Profile',
      action: '/profile/lana'
    }, {
      id: 12,
      type: 'behavioral',
      title: '👋 Zoey waved at you',
      content: 'She noticed you visited her profile 3 times this week.',
      time: '1 hour ago',
      read: false,
      avatar: 'https://uploadthingy.s3.us-west-1.amazonaws.com/fnQWPJWMus62iBnJCYdU4W/z1.png',
      sender: 'Zoey',
      cta: 'Wave Back',
      action: '/profile/zoey'
    }, {
      id: 13,
      type: 'premium',
      title: '💋 Bonny Blue sent you a kiss',
      content: 'Unlock this special interaction to see what happens next...',
      time: '3 hours ago',
      read: true,
      avatar: 'https://uploadthingy.s3.us-west-1.amazonaws.com/jq4Dkth7vgkmZJ8GZmzWDP/bonnyblue.png',
      cta: 'Respond',
      action: '/chat/bonny-blue',
      category: 'premium'
    },
    // Additional rewards feed items
    {
      id: 14,
      type: 'gift',
      title: '🎁 Kim Holland sent you a gift',
      content: 'She appreciates your support and sent you a special photo set.',
      time: '4 hours ago',
      read: false,
      avatar: 'https://uploadthingy.s3.us-west-1.amazonaws.com/bZ3iVXv5nZEMY6F847weq5/kimholland.png',
      cta: 'Open Gift',
      action: '/gifts/kim-holland',
      category: 'reward'
    }, {
      id: 15,
      type: 'tokens',
      title: '💰 Weekly Bonus: 50 LuvTokens',
      content: 'Your premium membership earned you a weekly bonus.',
      time: '1 day ago',
      read: true,
      amount: 50,
      cta: 'Claim Tokens',
      action: '/rewards/weekly-bonus',
      category: 'reward'
    }, {
      id: 16,
      type: 'achievement',
      title: '🏆 Achievement: Generous Tipper',
      content: "You've sent gifts to 5 different angels. They appreciate your generosity!",
      time: '2 days ago',
      read: true,
      cta: 'View Badge',
      action: '/achievements/generous-tipper',
      category: 'reward'
    }, {
      id: 17,
      type: 'reward',
      title: '🔥 Loyalty Reward: VIP Weekend',
      content: 'Enjoy exclusive access to all premium content this weekend.',
      time: '2 days ago',
      read: true,
      cta: 'Activate Reward',
      action: '/rewards/vip-weekend',
      category: 'reward'
    }];
    setNotifications(mockNotifications);
    // Simulate an alert after a short delay
    const timer = setTimeout(() => {
      if (Math.random() > 0.5) {
        setCurrentAlert(mockNotifications[2]);
        setShowAlert(true);
      }
    }, 3000);
    return () => clearTimeout(timer);
  }, []);
  if (!isOpen) return null;
  const filteredNotifications = notifications.filter(notification => {
    // First filter by tab
    if (activeTab === 'all') {
      // No tab filtering
    } else if (activeTab === 'activity' && !['message', 'behavioral', 'fanclub', 'premium'].includes(notification.type)) {
      return false;
    } else if (activeTab === 'rewards' && !['reward', 'achievement', 'tokens', 'gift'].includes(notification.type)) {
      return false;
    }
    // Then filter by category
    if (filter === 'all') {
      return true;
    }
    return notification.category === filter;
  });
  const getNotificationIcon = (type: NotificationType) => {
    switch (type) {
      case 'message':
        return <div className="w-10 h-10 rounded-full bg-pink-100 dark:bg-pink-900/30 flex items-center justify-center text-pink-500 dark:text-pink-400" data-id="element-15084">
            <MessageCircle size={18} data-id="element-15085" />
          </div>;
      case 'achievement':
        return <div className="w-10 h-10 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center text-yellow-500 dark:text-yellow-400" data-id="element-15086">
            <Trophy size={18} data-id="element-15087" />
          </div>;
      case 'gift':
        return <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-purple-500 dark:text-purple-400" data-id="element-15088">
            <Gift size={18} data-id="element-15089" />
          </div>;
      case 'tokens':
        return <div className="w-10 h-10 rounded-full bg-pink-100 dark:bg-pink-900/30 flex items-center justify-center text-pink-500 dark:text-pink-400" data-id="element-15090">
            <Heart size={18} data-id="element-15091" />
          </div>;
      case 'reward':
        return <div className="w-10 h-10 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center text-yellow-500 dark:text-yellow-400" data-id="element-15092">
            <Star size={18} data-id="element-15093" />
          </div>;
      case 'premium':
        return <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-purple-500 dark:text-purple-400" data-id="element-15094">
            <Flame size={18} data-id="element-15095" />
          </div>;
      case 'fanclub':
        return <div className="w-10 h-10 rounded-full bg-pink-100 dark:bg-pink-900/30 flex items-center justify-center text-pink-500 dark:text-pink-400" data-id="element-15096">
            <Crown size={18} data-id="element-15097" />
          </div>;
      case 'quest':
        return <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-500 dark:text-blue-400" data-id="element-15098">
            <Sparkles size={18} data-id="element-15099" />
          </div>;
      case 'feature':
        return <div className="w-10 h-10 rounded-full bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center text-teal-500 dark:text-teal-400" data-id="element-15100">
            <Zap size={18} data-id="element-15101" />
          </div>;
      case 'behavioral':
        return <div className="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center text-indigo-500 dark:text-indigo-400" data-id="element-15102">
            <Eye size={18} data-id="element-15103" />
          </div>;
      default:
        return <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-gray-500 dark:text-gray-400" data-id="element-15104">
            <Bell size={18} data-id="element-15105" />
          </div>;
    }
  };
  const renderAvatar = (notification: Notification) => {
    if (notification.avatar) {
      return <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 border-2 border-white dark:border-gray-800 shadow-sm" data-id="element-15106">
          <img src={notification.avatar} alt={notification.sender || 'User'} className="w-full h-full object-cover" data-id="element-15107" />
        </div>;
    }
    return getNotificationIcon(notification.type);
  };
  return <div className={cn(inSidebar ? 'w-full h-full border-r border-gray-200 dark:border-gray-800' : 'absolute right-0 top-full mt-2 w-96 max-h-[85vh] shadow-xl rounded-xl border border-gray-200 dark:border-gray-700 z-50', 'bg-white dark:bg-gray-900 overflow-hidden flex flex-col')} data-id="element-15108">
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800" data-id="element-15109">
        <h3 className="font-semibold text-gray-900 dark:text-white text-lg" data-id="element-15110">
          Notifications
        </h3>
        <div className="flex items-center gap-3" data-id="element-15111">
          <span className="text-xs px-2.5 py-1 bg-pink-100 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400 rounded-full font-medium" data-id="element-15112">
            2 new
          </span>
          <button className="text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400 w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-800" onClick={onClose} data-id="element-15113">
            <X size={18} data-id="element-15114" />
          </button>
        </div>
      </div>
      <div className="border-b border-gray-200 dark:border-gray-800 px-4 py-2" data-id="element-15115">
        <Tabs defaultValue="all" onValueChange={value => setActiveTab(value as any)} data-id="element-15116">
          <TabsList className="w-full bg-gray-100 dark:bg-gray-800 rounded-lg p-1" data-id="element-15117">
            <TabsTrigger value="all" className="flex-1 rounded-md data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700" data-id="element-15118">
              All
            </TabsTrigger>
            <TabsTrigger value="activity" className="flex-1 rounded-md data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700" data-id="element-15119">
              <span className="flex items-center gap-1.5" data-id="element-15120">
                <Clock size={14} data-id="element-15121" />
                Activity
              </span>
            </TabsTrigger>
            <TabsTrigger value="rewards" className="flex-1 rounded-md data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700" data-id="element-15122">
              <span className="flex items-center gap-1.5" data-id="element-15123">
                <Trophy size={14} data-id="element-15124" />
                Rewards
              </span>
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      <div className="flex items-center justify-between p-3 border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 relative" data-id="element-15125">
        <div className="flex items-center gap-2" data-id="element-15126">
          <Filter size={14} className="text-gray-500 dark:text-gray-400" data-id="element-15127" />
          <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-15128">
            Filter:
          </span>
        </div>
        <button onClick={() => scrollFilters('left')} className="absolute left-20 top-1/2 -translate-y-1/2 z-10 w-6 h-6 flex items-center justify-center bg-white/90 dark:bg-gray-800/90 rounded-full text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 shadow-sm" data-id="element-15129">
          <ChevronLeft size={14} data-id="element-15130" />
        </button>
        <div ref={filterBarRef} className="flex items-center gap-1.5 overflow-x-auto scrollbar-hide ml-8 mr-8" data-id="element-15131">
          <button onClick={() => setFilter('all')} className={cn('px-2.5 py-1 text-xs rounded-full whitespace-nowrap', filter === 'all' ? 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white font-medium' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800')} data-id="element-15132">
            All
          </button>
          <button onClick={() => setFilter('premium')} className={cn('px-2.5 py-1 text-xs rounded-full whitespace-nowrap', filter === 'premium' ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 font-medium' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800')} data-id="element-15133">
            Premium
          </button>
          <button onClick={() => setFilter('reward')} className={cn('px-2.5 py-1 text-xs rounded-full whitespace-nowrap', filter === 'reward' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400 font-medium' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800')} data-id="element-15134">
            Rewards
          </button>
          <button onClick={() => setFilter('fanclub')} className={cn('px-2.5 py-1 text-xs rounded-full whitespace-nowrap', filter === 'fanclub' ? 'bg-pink-100 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400 font-medium' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800')} data-id="element-15135">
            Fan Club
          </button>
          <button onClick={() => setFilter('quest')} className={cn('px-2.5 py-1 text-xs rounded-full whitespace-nowrap', filter === 'quest' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 font-medium' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800')} data-id="element-15136">
            Quests
          </button>
        </div>
        <button onClick={() => scrollFilters('right')} className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-6 h-6 flex items-center justify-center bg-white/90 dark:bg-gray-800/90 rounded-full text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 shadow-sm" data-id="element-15137">
          <ChevronRight size={14} data-id="element-15138" />
        </button>
      </div>
      <div className="overflow-y-auto flex-1 scrollbar-hide" data-id="element-15139">
        {filteredNotifications.length === 0 ? <div className="p-8 text-center" data-id="element-15140">
            <div className="w-16 h-16 mx-auto bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4" data-id="element-15141">
              <Bell size={28} className="text-gray-400 dark:text-gray-500" data-id="element-15142" />
            </div>
            <p className="text-gray-500 dark:text-gray-400 font-medium" data-id="element-15143">
              No notifications
            </p>
            <p className="text-gray-400 dark:text-gray-500 text-sm mt-1" data-id="element-15144">
              Check back later for updates
            </p>
          </div> : <div className="divide-y divide-gray-100 dark:divide-gray-800" data-id="element-15145">
            {filteredNotifications.map(notification => <div key={notification.id} className={cn('p-4 flex gap-3', !notification.read ? 'bg-pink-50/50 dark:bg-pink-900/10' : 'bg-white dark:bg-gray-900')} data-id="element-15146">
                {renderAvatar(notification)}
                <div className="flex-1 min-w-0" data-id="element-15147">
                  <div className="flex justify-between items-start" data-id="element-15148">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white line-clamp-1" data-id="element-15149">
                      {notification.title}
                    </h4>
                    <span className="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap ml-2 flex-shrink-0" data-id="element-15150">
                      {notification.time}
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-300 mt-1 line-clamp-2" data-id="element-15151">
                    {notification.content}
                  </p>
                  <div className="mt-3 flex justify-between items-center" data-id="element-15152">
                    {notification.cta && <button className="text-xs font-medium px-3 py-1.5 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-full hover:shadow-md hover:shadow-pink-500/20 transition-all" data-id="element-15153">
                        {notification.cta}
                      </button>}
                    <button className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400 ml-auto" data-id="element-15154">
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>)}
          </div>}
      </div>
      <div className="p-3 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 flex justify-between items-center" data-id="element-15155">
        <button className="text-xs text-pink-500 dark:text-pink-400 hover:underline font-medium" data-id="element-15156">
          Mark all as read
        </button>
        <button className="text-xs text-gray-600 dark:text-gray-300 flex items-center font-medium" data-id="element-15157">
          Notification Settings
          <ChevronRight size={14} className="ml-1" data-id="element-15158" />
        </button>
      </div>
      <style jsx data-id="element-15159">{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>;
};